import React, { ReactNode, ElementType, FC } from 'react'
import { Box, BoxProps } from '@chakra-ui/react'
import { FormattedMessage } from 'react-intl'

import { AmhUnit, HeightUnit, WeightUnit } from '@gaia-family/lib'

import * as Checkbox from '../Checkbox'
import { AmhProps as AmhInputProps } from '../Amh'
import { HeightProps as HeightInputProps } from '../Height'
import { WeightProps as WeightInputProps } from '../Weight'
import { Text } from '../../typography/Text'
import { Label } from '../Label'
import { SelectProps as SelectInputProps } from '../Select'

type FieldInputProps = {
  label?: ReactNode
  helperText?: string
  wrapProps?: BoxProps
}
type FieldProps<T> = T & FieldInputProps

const makePlaceholderComponent = function <T>(InputComponent: ElementType) {
  const FieldComponent = ({
    label,
    helperText,
    wrapProps = {},
    ...props
  }: FieldProps<T>) => {
    return (
      <Box {...wrapProps}>
        {(label || helperText) && (
          <Label label={label} helperText={helperText} />
        )}
        <InputComponent {...props} />
      </Box>
    )
  }
  FieldComponent.displayName = `Field.${
    // @ts-ignore
    InputComponent.displayName || 'Unknown'
  }`
  return FieldComponent
}

export type PlaceholderProps<T> = {
  value?: T | null
  placeholder: ReactNode
}

export type DateProps = PlaceholderProps<string>
const Date: FC<PlaceholderProps<string>> = ({ value, placeholder }) => {
  if (!value)
    return (
      <Text color="dark.gray" opacity={0.5}>
        {placeholder}
      </Text>
    )

  const [year, month, day] = value.split('-')
  return (
    <Text letterSpacing={6}>
      <FormattedMessage
        values={{ day, month, year }}
        defaultMessage="{day}/{month}/{year}"
      />
    </Text>
  )
}

export type BasicPlaceholderProps = PlaceholderProps<string | number>
const BasicPlaceholder: FC<BasicPlaceholderProps> = ({
  value,
  placeholder,
}) => {
  if (value === null || value === undefined)
    return (
      <Text color="dark.gray" opacity={0.5}>
        {placeholder}
      </Text>
    )

  return <Text>{value}</Text>
}

export type OptionedPlaceholderProps = Pick<
  SelectInputProps,
  'options' | 'value' | 'placeholder'
>
const OptionedPlaceholder = ({
  value,
  options,
  placeholder,
}: OptionedPlaceholderProps) => {
  const str = options.find(({ value: val }) => val === value)?.name || 'Unset'
  return <BasicPlaceholder placeholder={placeholder} value={str} />
}

export type CheckboxGroupProps = PlaceholderProps<readonly string[]> &
  Omit<Checkbox.CheckboxGroupProps, 'value' | 'onChange'>
const CheckboxGroup: FC<CheckboxGroupProps> = ({
  value,
  placeholder,
  options,
}) => {
  if (!value)
    return (
      <Text color="dark.gray" opacity={0.5}>
        {placeholder}
      </Text>
    )

  return (
    <Checkbox.CheckboxGroup
      onChange={() => {}}
      name=""
      options={options}
      value={value as string[]}
    />
  )
}

export type HeightProps = PlaceholderProps<HeightInputProps['value']>
const Height: FC<HeightProps> = ({ value: height, placeholder }) => {
  if (!height?.value)
    return (
      <Text color="dark.gray" opacity={0.5}>
        {placeholder}
      </Text>
    )

  if (height?.unit === HeightUnit.Inches) {
    const ftValue = Math.floor(height.value / 12)
    const inchValue = Math.floor(height.value % 12)

    if (!inchValue)
      return (
        <Text>
          <FormattedMessage
            values={{ ftValue }}
            defaultMessage="{ftValue} ft"
          />
        </Text>
      )

    if (!ftValue)
      return (
        <Text>
          <FormattedMessage
            values={{ inchValue }}
            defaultMessage="{inchValue} in"
          />
        </Text>
      )

    return (
      <Text>
        <FormattedMessage
          values={{ ftValue, inchValue }}
          defaultMessage="{ftValue} ft {inchValue} in"
        />
      </Text>
    )
  }

  if (height?.unit === HeightUnit.Centimetres) {
    return (
      <Text>
        <FormattedMessage
          values={{ cm: height?.value }}
          defaultMessage="{cm} cm"
        />
      </Text>
    )
  }

  return null
}

export type WeightProps = PlaceholderProps<WeightInputProps['value']>
const Weight: FC<WeightProps> = ({ value: weight }) => {
  if (!weight?.value)
    return (
      <Text color="dark.gray" opacity={0.5}>
        {/* TODO: Finalise copy */}
        <FormattedMessage defaultMessage="Not set" />
      </Text>
    )

  if (weight?.unit === WeightUnit.Kilograms) {
    const kg = weight.value
    return (
      <Text>
        <FormattedMessage values={{ kg }} defaultMessage="{kg} kg " />
      </Text>
    )
  }

  if (weight?.unit === WeightUnit.Pounds) {
    const lb = weight.value
    return (
      <Text>
        <FormattedMessage values={{ lb }} defaultMessage="{lb} lb " />
      </Text>
    )
  }

  return null
}

export type BinaryProps = PlaceholderProps<boolean>
const Binary: FC<BinaryProps> = ({ value, placeholder }) => {
  if (typeof value !== 'boolean')
    return (
      <Text color="dark.gray" opacity={0.5}>
        {placeholder}
      </Text>
    )

  return (
    <Text>
      {value ? (
        <FormattedMessage defaultMessage="Yes" />
      ) : (
        <FormattedMessage defaultMessage="No" />
      )}
    </Text>
  )
}
export type AmhProps = PlaceholderProps<AmhInputProps['value']>
const Amh: FC<AmhProps> = ({ value, placeholder }) => {
  if (value?.unit === AmhUnit.NanogramsPerMilliliter)
    return (
      <Text>
        <FormattedMessage
          values={{ value: value.value }}
          defaultMessage="{value} ng/ml"
        />
      </Text>
    )
  if (value?.unit === AmhUnit.PicomolesPerLitre)
    return (
      <Text>
        <FormattedMessage
          values={{ value: value.value }}
          defaultMessage="{value} pmol/L"
        />
      </Text>
    )

  return (
    <Text color="dark.gray" opacity={0.5}>
      {placeholder}
    </Text>
  )
}

export const Placeholders = {
  Text: makePlaceholderComponent<BasicPlaceholderProps>(BasicPlaceholder),
  Textarea: makePlaceholderComponent<BasicPlaceholderProps>(BasicPlaceholder),

  Weight: makePlaceholderComponent<WeightProps>(Weight),
  Height: makePlaceholderComponent<HeightProps>(Height),
  Amh: makePlaceholderComponent<AmhProps>(Amh),
  Number: makePlaceholderComponent<BasicPlaceholderProps>(BasicPlaceholder),

  Select: makePlaceholderComponent<BasicPlaceholderProps>(OptionedPlaceholder),
  Date: makePlaceholderComponent<DateProps>(Date),
  CheckboxGroup: makePlaceholderComponent<CheckboxGroupProps>(CheckboxGroup),
  RadioGroup:
    makePlaceholderComponent<BasicPlaceholderProps>(OptionedPlaceholder),
  BinaryInput: makePlaceholderComponent<BinaryProps>(Binary),
}
